import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Mail } from "lucide-react";
import { SITE } from "@/constants/site";
import { OFFERS } from "@/data/offers";
import { OFFERS_EN } from "@/data/offers.en";
import { useLang } from "@/i18n/LangContext";

export const Footer = () => {
  const { t, l, lang } = useLang();
  const offers = lang === "en" ? OFFERS_EN : OFFERS;

  return (
  <footer className="relative overflow-hidden bg-ink text-cream grain" data-testid="footer">
    <div className="mx-auto max-w-7xl px-5 md:px-10 pt-20 md:pt-28 pb-10">
      <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr] lg:gap-10">
        <div className="relative">
          <img
            src="/assets/logo-consultante-strategie-de-marque-pme.png"
            alt={t.logoAlt}
            className="h-24 w-24 rounded-3xl object-cover"
          />
          <p className="mt-7 font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-cream">
            {t.footer.tagline}
          </p>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="footer-email"
            className="group mt-5 inline-flex items-center gap-3 whitespace-nowrap font-display text-lg md:text-2xl font-bold tracking-tight text-terracotta transition-colors duration-300 hover:text-terracotta-light"
          >
            {SITE.email}
            <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-cream/60">
            {t.footer.description}
          </p>
          <a
            href={SITE.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-linkedin"
            aria-label={t.footer.linkedinAria}
            className="mt-8 inline-flex h-12 w-12 items-center justify-center rounded-full border border-cream/20 text-cream transition-colors duration-300 hover:bg-terracotta hover:text-ink hover:border-terracotta"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>

        <nav aria-label={t.footer.navAria}>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/40">{t.footer.navTitle}</p>
          <ul className="mt-6 space-y-3.5">
            {t.nav.links.map((link) => (
              <li key={link.path}>
                <Link
                  to={l(link.path)}
                  data-testid={`footer-nav-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="text-sm text-cream/70 transition-colors duration-200 hover:text-terracotta"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label={t.footer.servicesAria}>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/40">{t.footer.servicesTitle}</p>
          <ul className="mt-6 space-y-3.5">
            {offers.map((offer) => (
              <li key={offer.slug}>
                <Link
                  to={l(`/services/${offer.frSlug || offer.slug}`)}
                  data-testid={`footer-offer-${offer.frSlug || offer.slug}`}
                  className="text-sm text-cream/70 transition-colors duration-200 hover:text-terracotta"
                >
                  {offer.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to={l("/contact")}
                data-testid="footer-devis"
                className="inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-light transition-colors duration-200"
              >
                <Mail className="w-4 h-4" /> {t.common.requestQuote}
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-cream/10 pt-8 text-xs text-cream/45">
        <p>© {new Date().getFullYear()} {t.footer.copyright}</p>
        <div className="flex flex-wrap gap-6">
          <Link to={l("/mentions-legales")} data-testid="footer-legal" className="hover:text-terracotta transition-colors duration-200">
            {t.footer.legal}
          </Link>
          <Link to={l("/politique-de-confidentialite")} data-testid="footer-privacy" className="hover:text-terracotta transition-colors duration-200">
            {t.footer.privacy}
          </Link>
        </div>
      </div>
    </div>
  </footer>
  );
};
